import { Axios, AxiosError } from "axios"

export interface IResponseError {
    errorMessage?: string,
    statusCode?: number
}

export interface IResponseBase {
    error?: IResponseError
}

export interface IApiResponseError {
    status: number,
    errorCode: number,
    error: string
}

export interface IApiLink {
    name: string,
    href: string,
    rel?: string
}

export interface IApiResource {
    id: number,
    href?: string,
    timestamp?: string,
    links?: IApiLink[]
}

export interface IDTResourceCollectionMeta {
    count: number,
    offset: number,
    limit: number
}

export interface IDTResourceCollection<T> {
    meta: IDTResourceCollectionMeta,
    items: T[],
    links?: IApiLink[]
}

export class ItemLocation {
    projectId: number = 0;
    archiveId: number = 0;
    bucketId: number = 0;
    datasetId: number = 0;

    constructor(locationString?: string) {
        this.setFromLocation(locationString);
    }

    set(projectId: number, archiveId?: number, bucketId?: number, datasetId?: number): ItemLocation {
        this.projectId = projectId;
        if (archiveId !== undefined)
            this.archiveId = archiveId;
        if (bucketId !== undefined)
            this.bucketId = bucketId;
        if (datasetId !== undefined)
            this.datasetId = datasetId;
        return this;
    }

    setProject(id: number): ItemLocation {
        this.projectId = id;
        return this;
    }

    setArchive(id: number): ItemLocation {
        this.archiveId = id;
        return this;
    }

    setBucket(id: number): ItemLocation {
        this.bucketId = id;
        return this;
    }

    setDataset(id: number): ItemLocation {
        this.datasetId = id;
        return this;
    }

    setFromLocation(locationString?: string): ItemLocation | null {
        if (!locationString)
            return null;

        let parts = this.chunkSubstr(locationString, 8);
        if (parts.length > 0)
            this.projectId = Number("0x" + parts[0]);
        if (parts.length > 1)
            this.archiveId = Number("0x" + parts[1]);
        if (parts.length > 2)
            this.bucketId = Number("0x" + parts[2]);
        if (parts.length > 3)
            this.datasetId = Number("0x" + parts[3]);
        return this;
    }

    getLocation(): string {
        return "" +
            this.asHexValue(this.projectId) +
            this.asHexValue(this.archiveId) +
            this.asHexValue(this.bucketId) +
            this.asHexValue(this.datasetId);
    }

    private asHexValue(value: number) {
        return value.toString(16).padStart(8, '0');
    }

    private chunkSubstr(str: string, size: number): string[] {
        const numChunks = Math.ceil(str.length / size);
        const chunks = new Array(numChunks);

        for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
            chunks[i] = str.substr(o, size);
        }
        return chunks;
    }
}

const isAxiosError = (error: any): boolean => {
    return error && error instanceof AxiosError;
}

const isResponseBase = (error: any): boolean => {
    return 'error' in error;
}

export const tryGetErrorMessage = (error: any): IResponseBase => {
    if (isResponseBase(error)) {
        return error as IResponseBase;
    }

    if (isAxiosError(error)) {
        if (!error || !error.response || !error.response.status)
            return {
                error: {
                        errorMessage: "an unknown error occurred",
                        statusCode: 0
                    }
                };

        
        if (error.response.data.hasOwnProperty("error")) {
            return {
                error: {
                    errorMessage: (error.response.data as IApiResponseError).error ?? "an unknown error occurred",
                    statusCode: error.response.status
                }
            };
        }
        return {
            error: {
                errorMessage: "an unknown error occurred",
                statusCode: error.response.status
            }
        };
    }

    if (error && error.response) {
        let errorMessage: any = error.response.data
        if (errorMessage && typeof errorMessage === "object" && errorMessage.hasOwnProperty("error")) {
            errorMessage = errorMessage.error
        }
        return {
            error: {
                errorMessage: errorMessage ?? "an unknown error occurred",
                statusCode: 0
            }
        };
    }

    if (error && error.hasOwnProperty("message"))
        return {
            error: {
                errorMessage: error.message ?? "an unknown error occurred",
                statusCode: 0
            }
        };

    return {
        error: {
            errorMessage: "an unknown error occurred",
            statusCode: 0
        }
    };
}
